// @ts-check
import ms from 'milliseconds'
import { createCrudProp } from '../utils/crud-prop'
import { sortByTimeIfExists } from '../utils/sort-by'
import { optionSelectCaseStartOrAppTimeDay } from './events'

const patientMeasurementProps = createCrudProp({
  rootKey: 'pm',
  sortFn: sortByTimeIfExists,
})

/**
 * @param {ManualMeasurement} initial - The initial properties to apply.
 * @returns {any} - The changes to apply to the case.
 */
export const actionAddPatientMeasurement = patientMeasurementProps.actionAdd

/**
 * @param {string} id - The id of the item to remove.
 * @returns {any} - The changes to apply to the case.
 */
export const actionRemovePatientMeasurement =
  patientMeasurementProps.actionRemove

/**
 * @param {string} id - The id of the item
 * @param {Partial<ManualMeasurement>} updates - The updates to apply.
 * @returns {any} - The changes to apply to the case.
 */
export const actionUpdatePatientMeasurement =
  patientMeasurementProps.actionUpdate

/** @type {CaseSelector<ManualMeasurementWithId[]>} */
export const selectPatientMeasurements = patientMeasurementProps.select

/**
 * @param {CaseData} caseData
 * @param {{ maxTime: number; minTime: number }} opts
 * @returns {ManualMeasurementWithId[]}
 */
export const optionSelectPatientMeasurementsWithinTime = (
  caseData,
  { maxTime, minTime }
) =>
  selectPatientMeasurements(caseData).filter(
    ({ time }) => time >= minTime && time <= maxTime
  )

/**
 * @param {CaseData} caseData
 * @param {{ maxTime: number; minTime: number }} opts
 * @returns {ManualMeasurementWithId | null}
 */
export const optionSelectLatestPatientMeasurementWithinTime = (
  caseData,
  opts
) => {
  const results = optionSelectPatientMeasurementsWithinTime(caseData, opts)
  // grab the last one
  return results[results.length - 1] || null
}

/**
 * Grabs the latest patient measurement up to, but not past, the passed in time.
 * If the useCaseStart option is passed in, it will use the case start time as
 * the max time if it is available.
 *
 * @param {CaseData} caseData
 * @param {{
 *   appTime?: number
 *   maxAgeDays?: number
 *   useCaseStart?: boolean
 * }} [opts]
 * @returns {ManualMeasurementWithId | null}
 */
export const optionSelectPatientMeasurementClosestToTime = (
  caseData,
  opts = {}
) => {
  const time = opts.appTime || Date.now()
  const maxDays = opts.maxAgeDays || 30
  const maxTime = opts.useCaseStart
    ? optionSelectCaseStartOrAppTimeDay(caseData, time)
    : time
  const minTime = maxTime - ms.days(maxDays)
  return optionSelectLatestPatientMeasurementWithinTime(caseData, {
    maxTime,
    minTime,
  })
}
